<template>
  <div class="profile container" v-loading="loading">
    <el-card>
      <div slot="header" class="card-header">
        <h2>个人中心</h2>
      </div>

      <el-row :gutter="20">
        <el-col :span="6">
          <div class="avatar-section">
            <div class="avatar-wrapper">
              <img v-if="avatarBase64" :src="avatarBase64" class="avatar" />
              <div v-else class="avatar-placeholder">
                {{ userInfo.user_name ? userInfo.user_name.charAt(0).toUpperCase() : 'U' }}
              </div>
            </div>
            <el-button type="text" size="small" @click="showEditAvatar = true">更换头像</el-button>
          </div>
        </el-col>

        <el-col :span="18">
          <div class="info-section">
            <el-descriptions :column="2" border>
              <el-descriptions-item label="用户名称">
                {{ userInfo.user_name || '-' }}
              </el-descriptions-item>
              <el-descriptions-item label="OpenID">
                {{ userInfo.user_open_id || '-' }}
              </el-descriptions-item>
              <el-descriptions-item label="邮箱">
                {{ userInfo.user_email || '-' }}
              </el-descriptions-item>
              <el-descriptions-item label="角色">
                <el-tag size="small">{{ userInfo.role || '普通用户' }}</el-tag>
              </el-descriptions-item>
              <el-descriptions-item label="状态">
                <el-tag size="small" type="success">正常</el-tag>
              </el-descriptions-item>
              <el-descriptions-item label="创建时间">
                {{ formatTime(userInfo.create_time) }}
              </el-descriptions-item>
            </el-descriptions>

            <div class="action-buttons">
              <el-button type="primary" @click="showEditDialog = true">
                <i class="el-icon-edit"></i> 编辑个人资料
              </el-button>
              <el-button type="warning" @click="showPasswordDialog = true">
                <i class="el-icon-lock"></i> 修改密码
              </el-button>
            </div>
          </div>
        </el-col>
      </el-row>
    </el-card>

    <!-- 最近登录日志 -->
    <el-card style="margin-top: 20px;">
      <div slot="header">
        <h3>最近登录日志</h3>
      </div>
      <el-table :data="loginLogs" style="width: 100%" v-loading="logsLoading">
        <el-table-column prop="login_time" label="登录时间" width="180">
          <template slot-scope="scope">
            {{ formatTime(scope.row.login_time) }}
          </template>
        </el-table-column>
        <el-table-column prop="ip_address" label="IP地址" width="150" />
        <el-table-column prop="device" label="设备" min-width="150" />
        <el-table-column prop="location" label="登录地点" min-width="150" />
        <el-table-column prop="status" label="状态" width="100">
          <template slot-scope="scope">
            <el-tag :type="scope.row.status === 'success' ? 'success' : 'danger'" size="small">
              {{ scope.row.status === 'success' ? '成功' : '失败' }}
            </el-tag>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 编辑个人资料对话框 -->
    <el-dialog title="编辑个人资料" :visible.sync="showEditDialog" width="500px">
      <el-form :model="editForm" :rules="editRules" ref="editForm" label-width="100px">
        <el-form-item label="用户名称" prop="user_name">
          <el-input v-model="editForm.user_name" placeholder="请输入用户名称" />
        </el-form-item>
        <el-form-item label="邮箱" prop="user_email">
          <el-input v-model="editForm.user_email" placeholder="请输入邮箱" type="email" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="showEditDialog = false">取消</el-button>
        <el-button type="primary" @click="handleSaveProfile" :loading="saving">保存</el-button>
      </div>
    </el-dialog>

    <!-- 修改密码对话框 -->
    <el-dialog title="修改密码" :visible.sync="showPasswordDialog" width="500px">
      <el-form :model="passwordForm" :rules="passwordRules" ref="passwordForm" label-width="100px">
        <el-form-item label="旧密码" prop="old_password">
          <el-input v-model="passwordForm.old_password" placeholder="请输入旧密码" show-password />
        </el-form-item>
        <el-form-item label="新密码" prop="new_password">
          <el-input v-model="passwordForm.new_password" placeholder="请输入新密码" show-password />
        </el-form-item>
        <el-form-item label="确认新密码" prop="confirm_password">
          <el-input v-model="passwordForm.confirm_password" placeholder="请再次输入新密码" show-password />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="showPasswordDialog = false">取消</el-button>
        <el-button type="primary" @click="handleChangePassword" :loading="changingPassword">修改</el-button>
      </div>
    </el-dialog>

    <!-- 更换头像对话框 -->
    <el-dialog title="更换头像" :visible.sync="showEditAvatar" width="400px">
      <div class="avatar-upload-content">
        <div class="avatar-preview" v-if="avatarPreviewUrl">
          <img :src="avatarPreviewUrl" class="preview-img" />
        </div>
        <div class="avatar-preview-placeholder" v-else>
          <i class="el-icon-picture-outline"></i>
          <p>请选择图片</p>
        </div>
        <el-upload
          class="avatar-uploader"
          action=""
          :auto-upload="false"
          :show-file-list="false"
          :on-change="handleAvatarChange"
          accept="image/*">
          <el-button type="primary" plain>
            <i class="el-icon-upload"></i> 选择图片
          </el-button>
        </el-upload>
        <p class="upload-tip">支持 JPG、PNG 格式，文件大小不超过 2MB</p>
      </div>
      <div slot="footer">
        <el-button @click="showEditAvatar = false">取消</el-button>
        <el-button type="primary" @click="handleUploadAvatar" :loading="avatarLoading">确认上传</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getCurrentUser, changePassword, uploadAvatar, getAvatar } from '../api/user'

export default {
  name: 'Profile',
  data() {
    const validateConfirmPassword = (rule, value, callback) => {
      if (value !== this.passwordForm.new_password) {
        callback(new Error('两次输入的密码不一致'))
      } else {
        callback()
      }
    }
    return {
      loading: false,
      logsLoading: false,
      saving: false,
      changingPassword: false,
      showEditDialog: false,
      showPasswordDialog: false,
      showEditAvatar: false,
      avatarLoading: false,
      avatarPreviewUrl: '',
      avatarFile: null,
      userInfo: {},
      avatarBase64: '',
      loginLogs: [],
      editForm: {
        user_name: '',
        user_email: ''
      },
      passwordForm: {
        old_password: '',
        new_password: '',
        confirm_password: ''
      },
      editRules: {
        user_name: [
          { required: true, message: '请输入用户名称', trigger: 'blur' }
        ],
        user_email: [
          { required: true, message: '请输入邮箱', trigger: 'blur' },
          { type: 'email', message: '请输入正确的邮箱格式', trigger: 'blur' }
        ]
      },
      passwordRules: {
        old_password: [
          { required: true, message: '请输入旧密码', trigger: 'blur' }
        ],
        new_password: [
          { required: true, message: '请输入新密码', trigger: 'blur' },
          { min: 6, message: '密码长度不能少于6位', trigger: 'blur' }
        ],
        confirm_password: [
          { required: true, message: '请再次输入新密码', trigger: 'blur' },
          { validator: validateConfirmPassword, trigger: 'blur' }
        ]
      }
    }
  },
  created() {
    this.loadUserInfo()
    this.loadLoginLogs()
  },
  methods: {
    async loadUserInfo() {
      this.loading = true
      try {
        const data = await getCurrentUser()
        this.userInfo = data || {}
        this.editForm.user_name = this.userInfo.user_name || ''
        this.editForm.user_email = this.userInfo.user_email || ''
        // 加载头像
        if (this.userInfo.avatar) {
          this.loadAvatar()
        }
      } catch (error) {
        console.error('获取用户信息失败:', error)
        this.$message.error('获取用户信息失败')
      } finally {
        this.loading = false
      }
    },
    async loadAvatar() {
      try {
        const res = await getAvatar()
        if (res.avatar_base64) {
          this.avatarBase64 = res.avatar_base64
        }
      } catch (error) {
        console.error('获取头像失败:', error)
      }
    },
    async loadLoginLogs() {
      this.logsLoading = true
      try {
        // 模拟登录日志数据
        this.loginLogs = [
          {
            login_time: new Date(Date.now() - 3600000).toISOString(),
            ip_address: '192.168.1.100',
            device: 'Chrome / Windows',
            location: '北京市',
            status: 'success'
          },
          {
            login_time: new Date(Date.now() - 86400000).toISOString(),
            ip_address: '192.168.1.100',
            device: 'Chrome / Windows',
            location: '北京市',
            status: 'success'
          },
          {
            login_time: new Date(Date.now() - 172800000).toISOString(),
            ip_address: '192.168.1.101',
            device: 'Safari / macOS',
            location: '上海市',
            status: 'success'
          }
        ]
      } catch (error) {
        console.error('获取登录日志失败:', error)
      } finally {
        this.logsLoading = false
      }
    },
    async handleSaveProfile() {
      this.$refs.editForm.validate(async (valid) => {
        if (!valid) return
        // 注意：后端暂未提供更新用户信息的 API，此功能暂不可用
        this.$message.info('该功能暂未开放')
        this.showEditDialog = false
      })
    },
    async handleChangePassword() {
      this.$refs.passwordForm.validate(async (valid) => {
        if (!valid) return
        this.changingPassword = true
        try {
          await changePassword({
            old_password: this.passwordForm.old_password,
            new_password: this.passwordForm.new_password
          })
          this.$message.success('密码修改成功')
          this.showPasswordDialog = false
          this.passwordForm = {
            old_password: '',
            new_password: '',
            confirm_password: ''
          }
        } catch (error) {
          console.error('修改密码失败:', error)
          this.$message.error(error.message || '修改密码失败')
        } finally {
          this.changingPassword = false
        }
      })
    },
    formatTime(time) {
      if (!time) return '-'
      return new Date(time).toLocaleString('zh-CN')
    },
    handleAvatarChange(file) {
      const isJPG = file.raw.type === 'image/jpeg'
      const isPNG = file.raw.type === 'image/png'
      const isLt2M = file.raw.size / 1024 / 1024 < 2

      if (!isJPG && !isPNG) {
        this.$message.error('只支持 JPG 或 PNG 格式的图片!')
        return false
      }
      if (!isLt2M) {
        this.$message.error('图片大小不能超过 2MB!')
        return false
      }

      this.avatarFile = file.raw
      this.avatarPreviewUrl = URL.createObjectURL(file.raw)
      return true
    },
    async handleUploadAvatar() {
      if (!this.avatarFile) {
        this.$message.warning('请先选择图片')
        return
      }

      this.avatarLoading = true
      try {
        const formData = new FormData()
        formData.append('avatar', this.avatarFile)

        const res = await uploadAvatar(formData)
        const avatarUrl = res.avatar_url || res.data?.avatar_url

        if (avatarUrl) {
          // 更新本地用户信息
          this.userInfo.avatar = avatarUrl
          // 更新 Vuex store
          this.$store.commit('user/UPDATE_AVATAR', avatarUrl)
          // 重新加载头像
          await this.loadAvatar()
          this.$message.success('头像更换成功')
          this.showEditAvatar = false
          this.avatarPreviewUrl = ''
          this.avatarFile = null
        } else {
          this.$message.error('上传失败，请重试')
        }
      } catch (error) {
        console.error('上传头像失败:', error)
        this.$message.error(error.message || '上传头像失败')
      } finally {
        this.avatarLoading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.profile {
  padding: 20px;
  background-color: var(--bg-page);
  min-height: calc(100vh - 84px);
}

.card-header {
  h2 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    color: var(--text-primary);
  }
}

.avatar-section {
  text-align: center;
  padding: 20px;
}

.avatar-wrapper {
  margin-bottom: 12px;
}

.avatar {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  object-fit: cover;
}

.avatar-placeholder {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: var(--primary-gradient, linear-gradient(135deg, #667eea 0%, #764ba2 100%));
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 48px;
  color: var(--bg-card, #fff);
  margin: 0 auto;
}

.info-section {
  padding: 10px 0;
}

.action-buttons {
  margin-top: 20px;
  display: flex;
  gap: 12px;

  .el-button {
    margin-left: 0;
  }
}

.avatar-upload-content {
  text-align: center;
  padding: 20px;
}

.avatar-preview {
  width: 150px;
  height: 150px;
  margin: 0 auto 20px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px dashed var(--border-color, #dcdfe6);
}

.preview-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-preview-placeholder {
  width: 150px;
  height: 150px;
  margin: 0 auto 20px;
  border-radius: 50%;
  border: 2px dashed var(--border-color, #dcdfe6);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: var(--text-muted, #909399);

  i {
    font-size: 48px;
    margin-bottom: 10px;
  }
}

.avatar-uploader {
  margin-bottom: 10px;
}

.upload-tip {
  font-size: 12px;
  color: var(--text-muted, #909399);
  margin-top: 10px;
}
</style>
